// ============================================================
// api.js
// Fetch wrapper for the user portal's calls to the user-tenant
// Function App API (via APIM in production, direct to func host
// in dev). Single source of truth for HTTP — every page module
// imports from here.
//
// Auth posture (2026-05-12 onward, post-JWT-cutover):
//   All user-tenant Function App endpoints are wrapped with
//   withAuth({ tenant: 'user' }). Authentication is Bearer-only
//   via MSAL acquireTokenSilent — no function-key path. See
//   git history pre-2026-05-12 for the transitional dual-send
//   phase that this commit retired.
//
// invitations/validate is the one pre-MSAL caller — fired from
// /redeem before the user signs in. doAnonymousRequest skips
// token acquisition (no account exists) and sends the request
// without an Authorization header. The endpoint stays
// anonymous-by-design on the backend (it's a public token-
// validation surface — see discovery doc).
//
// Token acquisition flow (authenticated path):
//   1. acquireTokenSilent({ ...apiRequest, account }) — fast,
//      cached. Returns a fresh access token from MSAL's session
//      cache or transparently refreshes via the refresh token.
//   2. On InteractionRequiredAuthError:
//        federated  → acquireTokenRedirect (Secure Score blocks
//                     popups; /login/org return path).
//        External ID → the session is dead: evict the stale
//                     account and full-page navigate to
//                     /login?returnTo=… (2026-08-02 stale-session
//                     fix — see the inline comment in
//                     getAccessToken for why NOT popup/redirect).
//
// 401 retry: on any 401 from an authenticated call, force MSAL
// to drop its cached token (forceRefresh) and retry once.
// Covers a revoked refresh token, server-side scope changes,
// or token rotation mid-session. A second 401 surfaces as
// ApiError to the caller.
//
// Backend reference:
//   backend/func-carehub-api/src/shared/withAuth.js
//   docs/design/jwt-auth-design.md §§4–6
//   docs/operations/user-portal-jwt-migration-discovery.md
//
// No timeouts. No retry logic beyond the 401 case.
// ============================================================

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  apiRequest,
  federatedApiRequest,
  isExternalIdAccount,
  isFederatedAccount,
} from './authConfig';
import { clearExternalIdAccounts, redirectToLogin } from './session-recovery';
import { getSelectedClientId, clearSelectedClientId, CLIENT_SELECTION_HEADER } from './clientSelection';

const RAW_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';
const API_BASE = RAW_BASE_URL.replace(/\/+$/, '');

// ── MSAL instance reference ──────────────────────────────────
// Registered at bootstrap (index.js) before <App /> mounts. All
// authenticated API calls happen inside <MsalProvider>, so by
// the time doRequest runs, the instance is always set. The
// defensive throw catches the wrong bootstrap order during
// refactors.

let _msalInstance = null;
// Federated (direct-Entra) instance — Stage 2 dual-MSAL.
// Registered by index.js ONLY when the build constructed it
// (REACT_APP_FEDERATED_* injected). null in the dormant prod
// build — the federated branch of selectAuthContext() is then
// inert and getAccessToken behaves byte-for-byte as Phase 1.
let _federatedInstance = null;

// ── Teams SSO token provider ─────────────────────────────────
// Registered by TeamsEntryPoint when the SPA runs inside a
// Microsoft Teams host. A provider FUNCTION (not a static token)
// so getAccessToken can re-acquire on demand — teams-js
// authentication.getAuthToken() caches + refreshes the SSO token
// internally, so per-request acquisition is cheap and survives the
// ~1h token lifetime across a session. When set, getAccessToken
// returns this token and the MSAL path is never touched (inside the
// Teams iframe MSAL redirect/popup flows are constrained — that's
// the whole reason for Teams SSO). null on the browser surface, so
// every existing MSAL caller behaves byte-for-byte as before.
let _teamsTokenProvider = null;

export function setMsalInstance(instance) {
  _msalInstance = instance;
}

export function setFederatedInstance(instance) {
  _federatedInstance = instance;
}

// Register / clear the Teams SSO token provider. provider is
// `({ forceRefresh }) => Promise<string>`. clearTeamsTokenProvider
// is called on TeamsEntryPoint unmount so a later browser-surface
// mount in the same JS context never reuses a stale Teams token.
export function registerTeamsTokenProvider(provider) {
  _teamsTokenProvider = provider;
}

export function clearTeamsTokenProvider() {
  _teamsTokenProvider = null;
}

// Exposed so the /login dispatch (LoginPage) and the /login/org
// return (FederatedLoginReturn) can drive the imperatively-managed
// federated instance. Returns null when dormant.
export function getFederatedInstance() {
  return _federatedInstance;
}

function getMsalInstance() {
  if (!_msalInstance) {
    throw new Error(
      'apiClient: MSAL instance not set. Call setMsalInstance(...) at app bootstrap before any API call.'
    );
  }
  return _msalInstance;
}

// ── Per-session auth-context selector ────────────────────────
// A user is signed in through exactly ONE path; only that
// instance holds an account for its own authority. This selects
// the instance + API scope + account for the current session.
//
// DEFAULT = External ID: every existing user (Google /
// email-password / External ID) takes EXACTLY the Phase 1 path —
// the External ID instance + apiRequest. The federated branch is
// inert while _federatedInstance is null (dormant prod) and is
// only taken once a user has actually signed in via direct-Entra.
//
// Accounts are environment-filtered (FINDING gotcha #1 — the MSAL
// account cache is shared across both instances on one origin) —
// never getAllAccounts()[0] unfiltered.
function selectAuthContext() {
  if (_federatedInstance && federatedApiRequest) {
    const fedAccounts = _federatedInstance.getAllAccounts().filter(isFederatedAccount);
    if (fedAccounts.length > 0) {
      return {
        instance: _federatedInstance,
        scopes: federatedApiRequest,
        account: fedAccounts[0],
      };
    }
  }
  const ext = getMsalInstance();
  const extAccounts = ext.getAllAccounts().filter(isExternalIdAccount);
  return { instance: ext, scopes: apiRequest, account: extAccounts[0] || null };
}

// ── ApiError ──────────────────────────────────────────────────
// Single error type for both transport-level and HTTP-level
// failures. Mirrors the admin apiClient shape so behaviour is
// consistent across portals. Existing callers catch on
// `.message` which Error/ApiError both expose — no caller
// change required.
export class ApiError extends Error {
  constructor({ message, status, field = null, detail = null, reason = null, isNetworkError = false }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.field = field;
    this.detail = detail;
    // `reason` is the discriminator used by the backend's withAuth /
    // withEntitlement responses (e.g. 'access_expired',
    // 'module_not_in_role_bundle', 'agent_not_entitled',
    // 'read_only_access'). Surfaced separately so the UI can pivot
    // to the access-denied screen on the right cases without
    // string-matching against `.message`.
    this.reason = reason;
    this.isNetworkError = isNetworkError;
  }
}

// ── Token acquisition ────────────────────────────────────────

// Exported for the focused tests in api.test.js — getAccessToken's
// instance-branched InteractionRequiredAuthError fallback is
// load-bearing (federated → acquireTokenRedirect; External ID →
// stale-account eviction + front-door /login redirect) and the
// tests assert directly against it. NOT part of the public surface
// for app code; callers go through doRequest / getCurrentUser / etc.
export async function getAccessToken({ forceRefresh = false } = {}) {
  // ── Teams SSO path ─────────────────────────────────────────
  // When running inside a Teams host, the registered provider
  // supplies the Bearer token (an Entra SSO token from teams-js
  // authentication.getAuthToken). This short-circuits MSAL
  // entirely — there is no MSAL account in the Teams iframe, so
  // falling through to selectAuthContext below would throw
  // "Not signed in". When no provider is registered (the browser
  // surface), this block is skipped and the MSAL flow runs unchanged.
  if (_teamsTokenProvider) {
    const teamsToken = await _teamsTokenProvider({ forceRefresh });
    if (teamsToken) return teamsToken;
    throw new ApiError({
      message: 'Teams sign-in token unavailable. Please reopen the CareHub tab.',
      status: 401,
    });
  }

  // selectAuthContext defaults to the External ID instance +
  // apiRequest — byte-for-byte the Phase 1 path for every existing
  // user. The federated instance + workforce-API scope are chosen
  // only for a session signed in via the direct-Entra path.
  const { instance, scopes, account } = selectAuthContext();
  if (!account) {
    throw new ApiError({
      message: 'Not signed in. Sign in first, then retry.',
      status: 401,
    });
  }
  try {
    const result = await instance.acquireTokenSilent({
      ...scopes,
      account,
      forceRefresh,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      if (instance === _federatedInstance) {
        // Federated path: redirect-only — Secure Score policy blocks
        // popups in our tenant and client tenants. acquireTokenRedirect
        // navigates through ${origin}/login/org; the bootstrap's
        // handleRedirectPromise (index.js) consumes the response,
        // FederatedLoginReturn forwards back into the app. The in-app
        // route IS preserved across the redirect: we stash pathname+search
        // here and FederatedLoginReturn restores it (falling back to /app).
        // This path is hit only on refresh-token revocation / CA policy
        // change (the federated Login Request eagerly requests the
        // workforce-API scope, authConfig.js federatedLoginRequest).
        try {
          sessionStorage.setItem('federated.returnTo', window.location.pathname + window.location.search);
        } catch { /* private mode / storage disabled — fall back to /app */ }
        await instance.acquireTokenRedirect({ ...scopes, account });
        // Control never returns — page is navigating to AAD. Throw a
        // sentinel so callers don't proceed assuming a token is in hand.
        throw new ApiError({
          message: 'Redirecting to complete sign-in…',
          status: 0,
        });
      }
      // External ID path (2026-08-02 stale-session fix): the old
      // acquireTokenPopup fallback fired from a BACKGROUND FETCH —
      // no user gesture — so browsers routinely blocked it, and the
      // rejection propagated with no recovery while the dead account
      // stayed cached: /app admitted the user (account presence),
      // /login auto-bounced them away from its own form, and only
      // closing the tab recovered. acquireTokenRedirect is NOT the
      // fix either: with no live ciamlogin session the authorize
      // trip routes IdP-credentialed users straight to their IdP,
      // skipping the picker page whose x-ms-cpim warm-up cookies the
      // return leg needs (AADSTS165000 — Gotchas #49/#50). A dead
      // External ID session goes back through the FRONT DOOR
      // instead: evict the stale account so account-presence checks
      // (RequireAuth / LoginPage) read signed-out, then full-page
      // navigate to /login?returnTo=<current route> — LoginPage
      // renders the form (its stale-probe guarantees it) with a
      // session-expired notice, and sign-in returns the user here.
      await clearExternalIdAccounts(instance);
      redirectToLogin({ sessionExpired: true });
      // Navigation is under way — throw a sentinel so callers don't
      // proceed assuming a token is in hand. Same shape as the
      // federated redirect sentinel above; AuthenticatedApp treats
      // status 0 + !isNetworkError as "navigating, keep quiet".
      throw new ApiError({
        message: 'Your session has expired. Redirecting to sign in…',
        status: 0,
      });
    }
    throw err;
  }
}

// ── Internals ────────────────────────────────────────────────

function buildHeaders({ token, method }) {
  const h = { 'accept': 'application/json' };
  if (method !== 'GET') {
    h['content-type'] = 'application/json';
  }
  if (token) {
    h['authorization'] = `Bearer ${token}`;
  }
  // Platform-05 — the client switcher. The selected Client is attached
  // here for api.js calls; the *-api.js modules build their own headers
  // and spread selectedClientHeaders() from utils/clientSelection.js
  // (until 2026-10-28 they did not, Gotcha #195). Omitted entirely when
  // no selection exists, which is the case for every single-client
  // user, so their requests are byte-identical to pre-Platform-05.
  // The server validates it against the caller's own rows and 403s
  // anything else — see utils/clientSelection.js.
  if (token) {
    const selectedClientId = getSelectedClientId();
    if (selectedClientId !== null) {
      h[CLIENT_SELECTION_HEADER] = String(selectedClientId);
    }
  }
  return h;
}

async function doFetch(url, method, body, headers) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (networkErr) {
    console.error(`API network error → ${method} ${url}`, networkErr);
    throw new ApiError({
      message: 'Network error - could not reach the API.',
      status: 0,
      isNetworkError: true,
    });
  }

  const bodyText = await response.text();
  let parsed = null;
  if (bodyText.length > 0) {
    try {
      parsed = JSON.parse(bodyText);
    } catch (parseErr) {
      const apiErr = new ApiError({
        message: `Server returned ${response.status} with non-JSON response`,
        status: response.status,
      });
      console.error(`API parse error → ${method} ${url}`, apiErr);
      throw apiErr;
    }
  }

  if (response.ok) {
    return { ok: true, parsed };
  }
  return { ok: false, status: response.status, parsed };
}

function throwFromResult(result, method, url) {
  const apiErr = new ApiError({
    message: (result.parsed && result.parsed.error)  || `Request failed with status ${result.status}`,
    status:  result.status,
    field:   (result.parsed && result.parsed.field)  || null,
    detail:  (result.parsed && result.parsed.detail) || null,
    reason:  (result.parsed && result.parsed.reason) || null,
  });
  console.error(`API error → ${method} ${url}`, apiErr);
  throw apiErr;
}

// Authenticated request: acquire token, attempt, retry on 401
// with forceRefresh, throw ApiError on any non-2xx after retry.
async function doRequest(url, method, body) {
  let token = await getAccessToken();
  let result = await doFetch(url, method, body, buildHeaders({ token, method }));

  if (!result.ok && result.status === 401) {
    token = await getAccessToken({ forceRefresh: true });
    result = await doFetch(url, method, body, buildHeaders({ token, method }));
  }

  // Platform-05 — stale client selection recovery. THE loop-breaker.
  //
  // withAuth fails closed on a client selection it cannot resolve
  // (403 client_not_permitted), which is correct: a silent fallback
  // would work in a different Client than the UI is displaying. But
  // failing closed means the client has to be what recovers, and a
  // stale selection in sessionStorage would otherwise 403 EVERY
  // request — including the bootstrap — locking the tab out of the
  // app with no way back.
  //
  // Causes: the row was deactivated, access was revoked, or the
  // value was hand-edited. Drop the selection, retry once without
  // it, and the caller lands on their default Client instead of a
  // dead end. Sibling of the 401 forceRefresh retry above.
  if (!result.ok && result.status === 403
      && result.parsed && result.parsed.error === 'client_not_permitted') {
    clearSelectedClientId();
    result = await doFetch(url, method, body, buildHeaders({ token, method }));
  }

  if (result.ok) return result.parsed;
  throwFromResult(result, method, url);
}

// Anonymous request: no MSAL session required. Used by
// invitations/validate — the one user-portal endpoint fired
// pre-sign-in (from the /redeem landing page). No
// Authorization or function-key header; the endpoint is
// anonymous-by-design at the backend (authLevel: 'anonymous').
async function doAnonymousRequest(url, method, body) {
  const result = await doFetch(url, method, body, buildHeaders({ token: null, method }));
  if (result.ok) return result.parsed;
  throwFromResult(result, method, url);
}

// ── Public surface ───────────────────────────────────────────
// Function signatures preserved verbatim — existing callers
// in CarerApp, ManagerApp, and RedeemPage unchanged.

// Current user profile (email, promptFragments). Called alongside
// getEntitlements() at bootstrap. Role/clientId/modules are now
// canonical on the entitlements endpoint; this call survives only
// to supply `email` and per-agent `promptFragments` which the
// entitlements endpoint does not return. See
// docs/design/entitlement-model.md for the split rationale.
export async function getCurrentUser() {
  return doRequest(`${API_BASE}/api/auth/me`, 'POST', undefined);
}

// Entitlements bootstrap. GET /api/me/entitlements — wrapped in
// withAuth only (NOT withEntitlement, since the endpoint is how
// the SPA discovers entitlements in the first place). Response:
//   { role, clientId, clientName, modules, accessExpiresAt,
//     expiresInDays }
// Drives role routing, sidebar filtering, and the auditor expiry
// banner. Called once on mount; refetch only on manual retry or
// re-auth — entitlement changes from the admin portal only land
// on the user's next sign-in.
export async function getEntitlements() {
  return doRequest(`${API_BASE}/api/me/entitlements`, 'GET', undefined);
}

// Client-visible audit trail (RM/NI only — Zuber ruling 2026-08-12).
// Generic pass-through of the filter object rather than a destructured
// allowlist: an allowlist is the shape that silently went stale twice on the
// rota reads (Gotcha #140), so anything the caller passes reaches the URL and
// empty values are simply omitted.
export async function listAuditTrail(filters = {}) {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  const q = qs.toString();
  return doRequest(`${API_BASE}/api/audit-trail${q ? `?${q}` : ''}`, 'GET', undefined);
}

// ── KPI targets (KPI layer Stage 3) ─────────────────────────────────
// Senior-gated BOTH ways (R6/R7): the settings surface is visible and
// editable only to an active NI / RM / Director / Owner, so the read is
// gated exactly like the write. Seeing a KPI's VALUE is unchanged and
// unrestricted - that is the Oversight tile, a different endpoint.
export async function listKpiTargets() {
  return doRequest(`${API_BASE}/api/kpi/targets`, 'GET', undefined);
}

// ⚠ ABSENT IS NOT CLEARED (Gotcha #178). Send ONLY the bands you mean to
// change: an omitted band keeps its stored value, an explicit null clears
// it back to the platform default. Never spread a whole form object in
// here - that would turn an untouched band into a deliberate clear.
export async function setKpiTarget(kpiKey, bands) {
  return doRequest(`${API_BASE}/api/kpi/targets/${encodeURIComponent(kpiKey)}`, 'PUT', bands);
}

// ── Reports (client-facing report registry, ruled 2026-08-21) ───────
// The reports THIS caller can run — availability is probed per report
// server-side with the SAME gates the run uses, so the page can never
// offer a report the download would refuse. The CSV download itself
// lives in utils/reports-api.js (blob path, not JSON).
export async function listReports() {
  return doRequest(`${API_BASE}/api/reports`, 'GET', undefined);
}

// ── Sample data in trial accounts (2026-10-20) ──────────────────────
// Manager or admin of their own client. getSampleData() is cheap
// ({ present }); getSampleData({ detail: true }) adds the removal preview.
// removeSampleData needs an explicit choice: records the customer added to
// sample people are deleted only when includeCustomerRecords is true.
export async function getSampleData({ detail = false } = {}) {
  return doRequest(`${API_BASE}/api/sample-data${detail ? '?detail=1' : ''}`, 'GET', undefined);
}
export async function removeSampleData(includeCustomerRecords) {
  return doRequest(`${API_BASE}/api/sample-data/remove`, 'POST', { includeCustomerRecords });
}

// ── Deletion requests (RM/NI only — the C4 raise/list pair) ─────────
// Backend: deletion-requests-create.js / deletion-requests-list.js.
// Raising a request deletes NOTHING — A2Z IT reviews it and actions an
// approved deletion manually; the list shows the outcome per row.
export async function listDeletionRequests() {
  return doRequest(`${API_BASE}/api/deletion-requests`, 'GET', undefined);
}

export async function createDeletionRequest({ entityType, entityId, reason }) {
  return doRequest(`${API_BASE}/api/deletion-requests`, 'POST', { entityType, entityId, reason });
}

// Platform-08 P8-4: the RM/NI "Your data export" surface (a terminating
// client's hand-over window). Summary + the notice-accepting download —
// noticeAccepted:true is REQUIRED by the backend before any link exists;
// the first download server-stamps receipt and starts the 30-day clock.
export async function getMyDataExport() {
  return doRequest(`${API_BASE}/api/me/data-export`, 'GET', undefined);
}
export async function downloadMyDataExport(exportId) {
  return doRequest(`${API_BASE}/api/me/data-export/${encodeURIComponent(exportId)}/download`, 'POST', { noticeAccepted: true });
}

// Public "Start free trial" form submission (FreeTrialPage). No
// MSAL session — the backend endpoint is anonymous-by-design. Body:
//   { firstName, lastName, email, organisationName, role, phone }
// Returns { ok: true, trialRequestId } on success; throws ApiError
// on validation (400) or server (500) failure.
export async function submitTrialRequest(body) {
  return doAnonymousRequest(`${API_BASE}/api/trial/request`, 'POST', body);
}

// Care Notes Quality
export async function evaluateCareNote({ messages, noteContent, clientId, clientPromptFragment }) {
  const body = messages
    ? { messages, clientId, clientPromptFragment }
    : { noteContent, clientId, clientPromptFragment };
  return doRequest(`${API_BASE}/api/carenotes/quality`, 'POST', body);
}

// NI/RM Conversational Agents (7 CareIQ agents, routed by agentRoute)
export async function callAgent({ agentRoute, messages, clientId, clientPromptFragment }) {
  return doRequest(`${API_BASE}/api/${agentRoute}`, 'POST', {
    messages,
    clientId,
    clientPromptFragment,
  });
}

// ── SSE streaming for the 7 CareIQ agents (Stage 3c) ─────────
// Parse one raw SSE event block (the text between two \n\n separators)
// into { event, data }. Per the SSE spec: an `event:` line sets the
// type (default 'message'); `data:` lines join with \n (a single
// leading space after the colon is stripped); lines starting with `:`
// are comments and ignored. Returns null when the block has no data.
// Exported for focused tests.
export function parseSSEEvent(rawBlock) {
  let event = 'message';
  const dataLines = [];
  for (const line of rawBlock.split('\n')) {
    if (line === '' || line.startsWith(':')) continue; // blank / comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  let data;
  try { data = JSON.parse(dataLines.join('\n')); } catch { data = null; }
  return { event, data };
}

// Streaming agent call. POSTs with Accept: text/event-stream and parses
// the SSE response with fetch + getReader() + TextDecoder({stream:true})
// (EventSource can't do POST + Authorization). Callbacks fire per event:
//   onStatus({message, phase}) · onContent({delta}) · onDone(meta) · onError({message, code, recoverable})
// Auth reuses getAccessToken (MSAL or Teams SSO) + the 401-once retry.
// Resolves when the stream ends (done / error / close / abort).
//
// Error routing:
//   - getAccessToken ApiError (e.g. not signed in) → THROWN (caller routes).
//   - non-2xx response → THROWN as ApiError (so the caller can route
//     access-denied via isAccessLost, 429, etc. — matches callAgent).
//   - network failure / mid-stream error event / premature close →
//     onError callback (in-bubble), NOT thrown.
//   - abort signal → resolves cleanly, no callback (user navigated away).
export async function callAgentStream({ agentRoute, messages, clientId, clientPromptFragment, signal, onStatus, onContent, onDone, onError }) {
  const url = `${API_BASE}/api/${agentRoute}`;
  const reqBody = JSON.stringify({ messages, clientId, clientPromptFragment });
  // This path builds its own headers (EventSource can't do POST +
  // Authorization), so it must mirror buildHeaders by hand.
  //
  // The old 'x-client-id' header is GONE (2026-08-11). It was the
  // APIM rate-limit counter-key until the 2026-08-05 rewrite moved
  // that onto the JWT oid, after which nothing read it — and this was
  // its last sender anywhere in the platform. It is removed from the
  // APIM CORS allowlist in the same commit: a header that is sent but
  // not allow-listed fails the preflight, so the two must always move
  // together.
  const headersFor = (token) => {
    const h = {
      'accept': 'text/event-stream',
      'content-type': 'application/json',
      'authorization': `Bearer ${token}`,
    };
    // Platform-05 — same contract as buildHeaders: attach the selected
    // Client only when the user has actually chosen one.
    const selectedClientId = getSelectedClientId();
    if (selectedClientId !== null) {
      h[CLIENT_SELECTION_HEADER] = String(selectedClientId);
    }
    return h;
  };

  let res;
  try {
    const token = await getAccessToken();
    res = await fetch(url, { method: 'POST', headers: headersFor(token), body: reqBody, signal });
    if (res.status === 401) {
      const token2 = await getAccessToken({ forceRefresh: true });
      res = await fetch(url, { method: 'POST', headers: headersFor(token2), body: reqBody, signal });
    }
  } catch (err) {
    if (err && err.name === 'AbortError') return;          // clean cancel
    if (err instanceof ApiError) throw err;                 // e.g. "Not signed in"
    onError && onError({ message: 'Could not reach the assistant. Check your connection and try again.', code: 'network', recoverable: true });
    return;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let parsed = null;
    if (text) { try { parsed = JSON.parse(text); } catch { /* non-JSON */ } }
    throw new ApiError({
      message: (parsed && parsed.error) || `Request failed with status ${res.status}`,
      status: res.status,
      reason: (parsed && parsed.reason) || null,
      detail: (parsed && parsed.detail) || null,
    });
  }

  if (!res.body || typeof res.body.getReader !== 'function') {
    onError && onError({ message: 'Streaming is not supported here. Please retry.', code: 'no_stream', recoverable: true });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let sawTerminal = false;

  const dispatch = (evt) => {
    if (!evt) return;
    if (evt.event === 'status') onStatus && onStatus(evt.data || {});
    else if (evt.event === 'content') onContent && onContent(evt.data || {});
    else if (evt.event === 'done') { sawTerminal = true; onDone && onDone(evt.data || {}); }
    else if (evt.event === 'error') { sawTerminal = true; onError && onError(evt.data || { message: 'The assistant reported an error.', code: 'stream_error', recoverable: true }); }
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }); // stream:true → multi-byte UTF-8 across chunks reassembles
      let sep;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const rawBlock = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        dispatch(parseSSEEvent(rawBlock));
      }
    }
    buffer += decoder.decode(); // flush any trailing bytes
    if (buffer.trim()) dispatch(parseSSEEvent(buffer)); // defensive: final block without trailing \n\n
  } catch (err) {
    if (err && err.name === 'AbortError') return;          // user navigated away — clean
    onError && onError({ message: 'The connection was interrupted. Please try again.', code: 'network', recoverable: true });
    return;
  }

  if (!sawTerminal) {
    onError && onError({ message: 'The connection closed before the response finished.', code: 'premature_close', recoverable: true });
  }
}

// Invitations: public token validation. Pre-MSAL, anonymous.
// Returns { valid: true, ...details } or { valid: false, reason }.
export async function validateInvitation(token) {
  return doAnonymousRequest(`${API_BASE}/api/invitations/validate`, 'POST', { token });
}

// Auth domain resolution. Pre-MSAL, anonymous. Powers the
// email-first /login flow (Pattern 3 / Step P): the SPA collects
// the user's work email, POSTs it here, and branches the MSAL
// loginRedirect on `provider`:
//   federation     -> domain_hint = response.domainHint
//   google         -> no domain_hint (see Gotcha #49)
//   email_password -> no domain_hint (External ID's default flow)
// No branch sends login_hint — see Gotcha #50. Users land on the
// External ID picker and type their email / pick the IdP tile
// there; login_hint would skip the picker and drop the x-ms-cpim
// warm-up cookies an external-IdP round-trip needs.
// See backend/.../auth-resolve-domain.js for the locked response
// shape and frontend/src/utils/login-page.helpers.js for the
// MSAL-request construction.
export async function resolveDomain(email) {
  return doAnonymousRequest(`${API_BASE}/api/auth/resolve-domain`, 'POST', { email });
}

// Invitations: redemption. Post-MSAL, authenticated.
// Returns { redeemed: true, ... } or { redeemed: false, reason }.
// Identity (oid + email) comes from the validated bearer token
// on the backend (identity.oid, identity.upn). Only token and
// the optional displayName are sent in the body.
export async function redeemInvitation({ token, displayName }) {
  return doRequest(`${API_BASE}/api/invitations/redeem`, 'POST', {
    token, displayName,
  });
}

// ── CareVoice public survey ──────────────────────────────────
// All three calls are anonymous-by-design at the backend
// (no withAuth wrapper). The survey page is reached by an
// anonymous respondent via a tokenised URL — there is no MSAL
// session.

export async function loadCareVoiceSurvey(cycleToken) {
  return doAnonymousRequest(
    `${API_BASE}/api/carevoice/survey/${encodeURIComponent(cycleToken)}`,
    'GET',
    undefined,
  );
}

export async function submitCareVoiceSurvey(cycleToken, body) {
  return doAnonymousRequest(
    `${API_BASE}/api/carevoice/survey/${encodeURIComponent(cycleToken)}`,
    'POST',
    body,
  );
}

export async function getCareVoiceBranding(clientSlug) {
  return doAnonymousRequest(
    `${API_BASE}/api/carevoice/branding/${encodeURIComponent(clientSlug)}`,
    'GET',
    undefined,
  );
}
